import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Typography } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import * as subMatFolderActions from '../../store/subMatFolder';

const PDF_ACCEPT = '.pdf,application/pdf';

export const parseSubMatUploadError = async (err) => {
  if (err?.serverResponse) {
    const sr = err.serverResponse;
    if (sr.errors?.length) return sr.errors;
    if (sr.message) return [sr.message];
  }
  if (err?.json) {
    try {
      const data = await err.json();
      if (data?.errors?.length) return data.errors;
      if (data?.message) return [data.message];
    } catch {
      /* ignore */
    }
  }
  return ['업로드에 실패했습니다.'];
};

const SubMatFolderUploadZone = ({
  folderId,
  disabled = false,
  onUploaded,
  onError,
  compact = false,
}) => {
  const dispatch = useDispatch();
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const uploadFile = useCallback(async (file) => {
    if (!folderId || !file || disabled) return;
    if (file.type && file.type !== 'application/pdf' && !file.name?.toLowerCase().endsWith('.pdf')) {
      onError?.(['PDF 파일만 업로드할 수 있습니다.']);
      return;
    }
    setUploading(true);
    try {
      await dispatch(subMatFolderActions.uploadFile(folderId, file));
      await dispatch(subMatFolderActions.selectFiles(folderId));
      onUploaded?.(folderId);
    } catch (err) {
      const messages = await parseSubMatUploadError(err);
      onError?.(messages);
    } finally {
      setUploading(false);
    }
  }, [dispatch, folderId, disabled, onUploaded, onError]);

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  if (compact) {
    return (
      <Button
        variant="outlined"
        component="label"
        size="small"
        disabled={disabled || uploading}
        startIcon={<CloudUpload />}
      >
        새 버전 업로드
        <input type="file" hidden accept={PDF_ACCEPT} onChange={handleInputChange} />
      </Button>
    );
  }

  return (
    <Box
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      sx={{
        border: 2,
        borderStyle: 'dashed',
        borderColor: dragOver ? 'primary.main' : 'divider',
        borderRadius: 1,
        bgcolor: dragOver ? 'action.hover' : 'background.default',
        p: 3,
        textAlign: 'center',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        transition: 'border-color 0.15s, background-color 0.15s',
      }}
    >
      <CloudUpload sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
      <Typography variant="body2" gutterBottom>
        PDF를 여기로 끌어다 놓거나
      </Typography>
      <Button
        variant="contained"
        component="label"
        size="small"
        disabled={uploading}
      >
        파일 선택
        <input type="file" hidden accept={PDF_ACCEPT} onChange={handleInputChange} />
      </Button>
      {uploading && (
        <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
          업로드 중…
        </Typography>
      )}
    </Box>
  );
};

export default SubMatFolderUploadZone;
